import Core from '../plugin-core-system/core.class.js';
import PluginController from '../plugin-core-system/plugin.controller.js';

export default class CsCookieBannerRenderer extends Core {

  static options = {
    layerPosition: 'center',
    language: 'de-DE',
    initializeErrMsg: 'Cookie Banner: Fehler beim Initialisieren',
    loadConfigErrMsg: 'Cookie Banner: Fehler beim Laden der JSON-Konfiguration',
    gtmCookieCategories: ['statistics', 'marketing'],
    path: {
      configJson: '/_assets/7b3ba8bb3871137dffa4d9cccacf730f/JavaScript/config/config-cookie-banner.json',
      contrastVersionCss: '/_assets/7b3ba8bb3871137dffa4d9cccacf730f/Css/template-contrast.css?1750927915'
    },
    selector: {
      globalWrapper: '',
      accordionClasses: ['js-accordion', 'js-cscb-accordion'],
      accordionItemClass: 'accordion__item',
      checkboxContainerClass: 'js-cscb-checkbox-container',
      templateHeaderPlaceholder: '.js-cscb-header-placeholder',
      templateHeaderId: 'cscb-template-header',
      templateTitlePlaceholder: '.js-cscb-title-placeholder',
      templateTitleId: 'cscb-template-title',
      templateButtonGroupPlaceholder: '.js-cscb-button-group-placeholder',
      templateButtonGroupId: 'cscb-template-button-group',
      templateFooterPlaceholder: '.js-cscb-footer-placeholder',
      templateFooterId: 'cscb-template-footer',
      templateCategoriesWrapperClass: 'cscb-edit-box__settings-wrapper',
      templateCategoriesPlaceholder: '.js-cscb-categories-placeholder',
      templateCategoryId: 'cscb-template-category-setting',
      templateCategoryWrapperClass: 'cscb-edit-box__settings-item',
      templateCookieTablePlaceholder: '.js-cscb-cookie-tables',
      templateCookieTableId: 'cscb-template-cookie-table',
      templateMediaCheckboxPlaceholder: '.js-cscb-cookie-media-items',
      templateMediaCheckboxId: 'cscb-template-media-checkbox',
      templateLayerPlaceholder: '.js-cscb-layer-placeholder',
      templateLayerId: 'cscb-template-layer',
      templateMediaPreviewId: 'cscb-template-media-preview',
      templateMediaPreviewPlaceholderClass: 'js-cscb-media-preview-placeholder',
      modalTitleId: 'modal-cscb-headline',
      modalContentWrapperSelector: '.js-cscb-edit-box .js-cs-modal-content-wrapper'
    }
  };



  /**
   * read settings
   */
  setLanguage() {
    this.language = document.getElementsByTagName('html')[0].getAttribute('lang');
    if (!this.language) {
      this.language = CsCookieBannerRenderer.options.language;
    }
  }

  async loadSettings() {
    const response = await fetch(CsCookieBannerRenderer.options.path.configJson);
    if (!response.ok) throw new Error(CsCookieBannerRenderer.options.loadConfigErrMsg);

    const configData = await response.json();

    this.data = configData['language'][this.language];
  }



  /**
   * general template rendering helper functions
   */
  renderTemplate(templateString, data = {}) {
    return templateString.replace(/\[\[([^\]]+)\]\]/g, (match, key) => {
      return typeof data[key] !== 'undefined' ? data[key] : '';
    });
  }

  hydrateDataLinks(containerEl) {
    containerEl.querySelectorAll('a[data-href]').forEach(a => {
      a.href = a.dataset.href;
      a.removeAttribute('data-href');
    });
  }
  renderPartialToSelector(selector, htmlString, root = document) {
    const el = root.querySelector(selector);
    if (!el) {
      console.warn(`Placeholder ${selector} not found.`);
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = htmlString;

    this.hydrateDataLinks(wrapper);

    const fragment = document.createDocumentFragment();
    Array.from(wrapper.childNodes).forEach(node => fragment.appendChild(node));

    el.replaceWith(fragment);
  }

  insertPartialFromTemplate(templateId, data, placeholderSelector, root = document) {

    // get template
    const template = document.getElementById(templateId);
    if (!template) return;

    // fill template with data
    const rendered = this.renderTemplate(template.innerHTML, data);

    this.renderPartialToSelector(placeholderSelector, rendered, root);
  }



  /**
   * cookie categories: specific template rendering helper functions
   */
  renderMediaItems(categoryNode, mediaData = {}) {
    const mediaWrapper = categoryNode.querySelector(CsCookieBannerRenderer.options.selector.templateMediaCheckboxPlaceholder);
    if (!mediaWrapper) return;

    const mediaKeys = Object.keys(mediaData);
    if (mediaKeys.length === 0) {
      mediaWrapper.remove();
      return;
    }

    const template = document.getElementById(CsCookieBannerRenderer.options.selector.templateMediaCheckboxId);
    if (!template) return;

    const renderedItems = mediaKeys.map(key => {
      const media = mediaData[key];

      return this.renderTemplate(template.innerHTML, {
        mediaKey: key,
        label: media.label || '',
        urlToPrivacy: media.urlToPrivacy || '#',
        dataProtectionExternalName: this.data.dataProtectionExternalName,
        dataProtectionAriaLabel: this.data.dataProtectionAriaLabel + ' ' + media.label
      });
    }).join('');

    this.renderPartialToSelector(
      CsCookieBannerRenderer.options.selector.templateMediaCheckboxPlaceholder,
      renderedItems,
      categoryNode
    );
  }

  renderCookieTables(categoryNode, cookies = []) {
    const placeholder = categoryNode.querySelector(CsCookieBannerRenderer.options.selector.templateCookieTablePlaceholder);
    if (!placeholder) return;

    if (!Array.isArray(cookies) || cookies.length === 0) {
      placeholder.remove();
      return;
    }

    const template = document.getElementById(CsCookieBannerRenderer.options.selector.templateCookieTableId);
    if (!template) return;

    const renderedTables = cookies.map(cookie => {
      const tableData = {
        nameTitle: this.data.nameTitle,
        name: cookie.name,
        link: cookie.link,
        dataProtectionExternalName: this.data.dataProtectionExternalName,
        purposeTitle: this.data.purposeTitle,
        purpose: cookie.purpose
      };

      return this.renderTemplate(template.innerHTML, tableData);
    }).join('');

    this.renderPartialToSelector(
      CsCookieBannerRenderer.options.selector.templateCookieTablePlaceholder,
      renderedTables,
      categoryNode
    );
  }

  renderCategoryItem(categoryKey, categoryData) {
    const template = document.getElementById(CsCookieBannerRenderer.options.selector.templateCategoryId);
    if (!template) return '';

    const data = {
      key: categoryKey,
      title: categoryData.title,
      contrastModeAdditionalTitle: categoryData.contrastModeAdditionalTitle,
      description: categoryData.description,
      tagManager: CsCookieBannerRenderer.options.gtmCookieCategories.includes(categoryKey) ? 'active' : 'inactive'
    };

    // render item with data
    const categoryNode = document.createElement('div');
    categoryNode.innerHTML = this.renderTemplate(template.innerHTML, data);
    categoryNode.classList.add(
      CsCookieBannerRenderer.options.selector.templateCategoryWrapperClass,
      CsCookieBannerRenderer.options.selector.accordionItemClass,
      CsCookieBannerRenderer.options.selector.checkboxContainerClass
    );

    // disable essential checkbox
    const checkboxEssential = categoryNode.querySelector('#cscb-essential');
    if (checkboxEssential) {
      checkboxEssential.disabled = true;
      checkboxEssential.checked = true;
    }

    return categoryNode;
  }

  renderCookieCategories(cookieSettings) {
    const placeholder = document.querySelector(CsCookieBannerRenderer.options.selector.templateCategoriesPlaceholder);
    if (!placeholder) return;

    const wrapperNode = document.createElement('div');
    wrapperNode.classList.add(CsCookieBannerRenderer.options.selector.templateCategoriesWrapperClass);
    CsCookieBannerRenderer.options.selector.accordionClasses.forEach(className => {
      wrapperNode.classList.add(className);
    });
    wrapperNode.dataset.accordionOptions = JSON.stringify({
      ignoreHash: true
    });

    placeholder.replaceWith(wrapperNode);

    Object.entries(cookieSettings).forEach(([key, data]) => {
      const categoryNode = this.renderCategoryItem(key, data);

      // render media checkboxes
      this.renderMediaItems(categoryNode, data.media);

      // render table data (e.g. statistics)
      this.renderCookieTables(categoryNode, data.cookies);

      // add cookie category to wrapper
      wrapperNode.appendChild(categoryNode);
    });
  }



  /**
   * build cookie banner - main function
   */
  buildCookieBanner() {
    const settings = CsCookieBannerRenderer.options;

    // insert header
    this.insertPartialFromTemplate(settings.selector.templateHeaderId, {
      closeButtonAriaLabel: this.data.closeButtonAriaLabel,
      contrastButtonIncreaseLabel: this.data.contrastButtonIncreaseLabel,
      contrastButtonResetLabel: this.data.contrastButtonResetLabel,
      contrastVersionPath: settings.path.contrastVersionCss
    }, settings.selector.templateHeaderPlaceholder);

    // insert title and description
    this.insertPartialFromTemplate(settings.selector.templateTitleId, {
      headline: this.data.headline,
      infoText: this.data.infoText,
      modalTitleId: settings.selector.modalTitleId
    }, settings.selector.templateTitlePlaceholder);

    // set aria-labelledby after title is inserted
    const modalContentWrapper = document.querySelector(settings.selector.modalContentWrapperSelector);
    if (modalContentWrapper) {
      modalContentWrapper.setAttribute('aria-labelledby', settings.selector.modalTitleId);
    }

    // insert button group
    this.insertPartialFromTemplate(settings.selector.templateButtonGroupId, {
      buttonAcceptText: this.data.buttonAcceptText,
      buttonSaveText: this.data.buttonSaveText,
      buttonDeclineText: this.data.buttonDeclineText
    }, settings.selector.templateButtonGroupPlaceholder);

    // insert footer
    this.insertPartialFromTemplate(settings.selector.templateFooterId, {
      dataProtectionLink: this.data.dataProtectionLink,
      dataProtectionName: this.data.dataProtectionName,
      imprintLink: this.data.imprintLink,
      imprintName: this.data.imprintName
    }, settings.selector.templateFooterPlaceholder);

    // insert cookie categories
    this.renderCookieCategories(this.data.cookieSettings);

    // insert layer template
    this.insertPartialFromTemplate(settings.selector.templateLayerId, {
      layerPosition: settings.layerPosition,
      layerInfoText: this.data.layerInfoText,
      buttonAcceptText: this.data.buttonAcceptText,
      buttonDeclineText: this.data.buttonDeclineText,
      buttonDenyText: this.data.buttonDenyText
    }, settings.selector.templateLayerPlaceholder);

    // fire event
    document.dispatchEvent(new CustomEvent('cscb:built'));
  }

  buildMediaPreviewBox(externalMediaElement, cookieName) {
    const settings = CsCookieBannerRenderer.options;

    // create placeholder node
    const placeholderNode = document.createElement('div');
    placeholderNode.classList.add(settings.selector.templateMediaPreviewPlaceholderClass);
    externalMediaElement.insertAdjacentElement('afterend', placeholderNode);

    // insert data if provided
    const previewBoxData = this.data.cookieSettings.externalMedia?.media[cookieName]?.previewBox;
    if (!previewBoxData) return;

    const placeholderSelector = `.${settings.selector.templateMediaPreviewPlaceholderClass}`;
    this.insertPartialFromTemplate(settings.selector.templateMediaPreviewId, {
      description: previewBoxData.description,
      buttonText: previewBoxData.button,
      cookieKey: cookieName
    }, placeholderSelector);

    // fire event
    document.dispatchEvent(new CustomEvent('cscb:preview-built'));
  }



  /**
   * main init function
   */
  init() {
    this.setLanguage();

    this.loadSettings().then(() => {
      this.buildCookieBanner();
    }).catch(error => {
      console.warn(CsCookieBannerRenderer.options.initializeErrMsg, error);

      // rejects all cookies in case of an initialization error
      this.cookieBannerHandler = PluginController.retrievePluginInstanceFromElement(document, 'CsModalManager');
      if (!this.cookieBannerHandler) return;

      this.cookieBannerHandler.declineAllCookies();
    });
  }
}
